import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  DROPDOWN_SIZE,
  DSButton,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
} from '@repo/design-system/index'
import classNames from 'classnames'
import { useState } from 'react'
import { HighlightOverviewResponse } from '../../../../../model/caf.interface'
import { useCafHighlightListV5 } from '../../../../../modules/apis/webview/caf'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import { s_review } from '../../../../../modules/i18n/strings/auto/wording'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import urls from '../../../../../modules/urls'
import { COLORS } from '../../../../../modules/vars'
import { AllHighlights, PinnedHighlights } from '../../common/HighlightSection'
import useHighlightSidePopup from '../popup/useHighlightSidePopup'

type FilterOption = 'all' | 'pinned'

const CAFOverviewHighlights = () => {
  const router = useAppRouter()
  const { lessonId, tab, tag, highlightId } = router.query
  const { t } = useTranslation()
  const { data, refetch, isFetched } = useCafHighlightListV5<HighlightOverviewResponse>({
    lessonId: +lessonId,
    pinned: false,
    recommended: false,
    show_details: false,
  })
  useHighlightSidePopup(tag, highlightId, false, () => {
    router.push(urls.student.newPortal.lessons.reviewDetail.asCafTag(lessonId, tab, null))
  })

  const options = [
    { label: t(n_caf.highlight_list.sort_optn1), value: 1, name: 'recommended' },
    { label: t(n_caf.highlight_list.sort_optn2), value: 2, name: 'count' },
    { label: t(n_caf.highlight_list.sort_optn3), value: 3, name: 'category' },
  ]
  const [sortOption, setSortOption] = useState(options[0])
  const [filter, setFilter] = useState<FilterOption>('all')
  const changeTagPopup = (tag: number, highlightId: number) => {
    if (tag >= 0 && highlightId >= 0) {
      router.push(urls.student.newPortal.lessons.reviewDetail.asCafTagAndId(lessonId, tab, tag, highlightId))
    } else if (tag >= 0) {
      router.push(urls.student.newPortal.lessons.reviewDetail.asCafTag(lessonId, tab, tag))
    } else {
      router.push(urls.student.newPortal.lessons.reviewDetail.asCaf(lessonId, tab))
    }
  }

  const listData =
    data?.highlights?.map((item) => {
      return {
        ...item,
        zscore_sum: item.zscore, //map
        average_count: item.count,
        tag: item.highlight_tag,
      }
    }) || []
  const pinnedListData = listData.filter((item) => item.is_pinned)

  return (
    <div className="mb-[100px] flex h-[100%] w-[100%] flex-col items-start justify-start pt-[32px]">
      <div className="flex flex-row items-start justify-start">
        <span className="text-[20px] font-bold">{t(n_caf.overview.menu_areas)}</span>
      </div>
      <Filter
        filter={filter}
        setFilter={setFilter}
        size={DROPDOWN_SIZE.MD}
        options={options}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />
      {filter === 'all' && <AllHighlights data={listData} sortOption={sortOption} setSelected={changeTagPopup} />}
      {filter === 'pinned' && (
        <PinnedHighlights
          data={pinnedListData}
          sortOption={sortOption}
          setSelected={changeTagPopup}
          setFilter={setFilter}
          infoText={t(n_caf.highlight_list_edit.toast4)}
          buttonText={t(n_caf.overview.tophighlights_edit)}
        />
      )}
    </div>
  )
}

type SortOption = {
  label: string
  value: number
  name: string
}
interface FilterProps {
  filter: FilterOption
  options: SortOption[]
  setFilter: (filter: FilterOption) => void
  size: DROPDOWN_SIZE
  sortOption: SortOption
  setSortOption: (option: SortOption) => void
}
const Filter = ({ filter, setFilter, size, options, sortOption, setSortOption }: FilterProps) => {
  const { t } = useTranslation()
  return (
    <div>
      <div className="mt-[16px] flex flex-row items-center justify-start">
        <Dropdown selected={sortOption} setSelected={setSortOption} size={size} className="mr-[8px]">
          <DropdownButton>{sortOption.label}</DropdownButton>
          <DropdownMenu>
            {options.map((option, i) => (
              <DropdownMenuItem key={i} value={option.value}>
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </Dropdown>
        <div className="flex h-[38px] flex-row items-center justify-center">
          <div className="h-[24px] w-[1px] bg-gray-200"></div>
        </div>
        <DSButton
          type={BUTTON_TYPE.OUTLINE}
          size={BUTTON_SIZE.SM}
          onClick={() => setFilter('all')}
          customBorderColor={filter === 'all' ? COLORS.PURPLE500 : COLORS.GRAY300}
          className={classNames('ml-[8px] !cursor-pointer !rounded-[8px]')}
        >
          <DSButton.Label
            className={classNames('whitespace-nowrap', {
              '!text-purple-500': filter === 'all',
              '!text-black': filter !== 'all',
            })}
          >
            {t(s_review.all)}
          </DSButton.Label>
        </DSButton>
        <DSButton
          type={BUTTON_TYPE.OUTLINE}
          size={BUTTON_SIZE.SM}
          onClick={() => setFilter('pinned')}
          customBorderColor={filter === 'pinned' ? COLORS.PURPLE500 : COLORS.GRAY300}
          className={classNames('ml-[8px] !cursor-pointer !rounded-[8px]')}
        >
          <DSButton.Label
            className={classNames('whitespace-nowrap', {
              '!text-purple-500': filter === 'pinned',
              '!text-black': filter !== 'pinned',
            })}
          >
            {t(n_caf.highlight_list_edit.title)}
          </DSButton.Label>
        </DSButton>
      </div>
    </div>
  )
}

export default CAFOverviewHighlights
